import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const ApprovalBlogCard = ({ title, excerpt, image, author, date, readMoreLink }) => {
    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.2,
        triggerOnce: true
    });

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
    }, [controls, inView]);

    const cardVariants = {
        hidden: { 
            opacity: 0, 
            y: 50,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1],
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };

    const contentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 0.4
            }
        }
    };

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={cardVariants}
            whileHover={{
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
            className="w-full max-w-2xl mx-auto flex flex-col bg-gradient-to-br from-white to-purple-50 p-8 rounded-2xl shadow-xl border border-purple-100 hover:border-purple-200 transition-all"
        >
            {/* Image with parallax effect container */}
            <motion.div 
                className="w-full h-64 md:h-80 overflow-hidden rounded-xl mb-6 relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
            >
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/10 to-purple-500/10 z-10 rounded-xl" />
                
                <motion.img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover rounded-xl"
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                />
            </motion.div>

            {/* Title with animated underline */}
            <motion.h3 
                className="text-3xl font-bold text-gray-800 mb-4 relative pb-2"
                variants={contentVariants}
            >
                {title}
                <motion.div 
                    className="absolute bottom-0 left-0 h-1 bg-purple-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                />
            </motion.h3>

            {/* Author and date */}
            <motion.div 
                className="flex items-center gap-3 mb-5"
                variants={contentVariants}
            >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                    {author.charAt(0)}
                </div>
                <div className="text-gray-600">
                    <p className="font-medium">By {author}</p>
                    {date && <p className="text-sm opacity-80">{date}</p>}
                </div>
            </motion.div>

            {/* Excerpt with animated appearance */}
            <motion.p 
                className="text-gray-600 mb-8 text-lg leading-relaxed"
                variants={contentVariants}
            >
                {excerpt}
            </motion.p>

            {/* Animated button */}
            <motion.div
                variants={contentVariants}
                className="mt-auto"
            >
                <motion.a
                    href={readMoreLink}
                    className="relative inline-block px-8 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium overflow-hidden group"
                    whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 10px 15px -3px rgba(124, 58, 237, 0.3)"
                    }}
                    whileTap={{ scale: 0.98 }}
                >
                    {/* Button shine effect */}
                    <span className="absolute top-0 left-0 w-full h-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                    
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        Read More
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-5 w-5 transition-transform group-hover:translate-x-1" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                        >
                            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </span>
                </motion.a>
            </motion.div>

            {/* Decorative elements */}
            <motion.div 
                className="absolute top-4 right-4 w-16 h-16 rounded-full bg-purple-100 opacity-30"
                animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />
        </motion.div>
    );
};

export default ApprovalBlogCard;